import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Check } from "lucide-react";
import { Field, BigButton } from "@/components/Field";
import { BackLink, Panel, SelectField, TextArea, Empty } from "@/components/Bits";
import { PageHeader } from "@/components/PageHeader";
import { OS_STATUS } from "@/config/os";
import { tipoIcon } from "@/config/equipamentos";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";

const EMPTY = {
  cliente_id: "", fazenda_id: "", equipamento_ids: [], categoria_id: "", servico_solicitado: "", problema_relatado: "",
  diagnostico: "", servico_realizado: "", tecnico_id: "", colaborador_ids: [], data: new Date().toISOString().slice(0, 10),
  horario: "", pecas: [], observacoes: "", km: "", horas_trabalhadas: "", valor_servico: "", status: "solicitada",
};

const Toggle = ({ on, onClick, children, testId, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className={`min-h-[56px] w-full rounded-xl border-2 px-3 flex items-center gap-3 text-left transition-colors ${on ? "border-[#1E4D2B] bg-[#E8F5E9]" : "border-[#E2DDD3] bg-white hover:border-[#1E4D2B]"}`}>
    <span className={`h-6 w-6 rounded-md border-2 flex items-center justify-center shrink-0 ${on ? "bg-[#1E4D2B] border-[#1E4D2B] text-white" : "border-[#C9C2B4]"}`}>{on && <Check className="h-4 w-4" />}</span>
    {children}
  </button>
);

const toggleIn = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const ProdutoPick = ({ p, onPick, disabled, i }) => {
  const { user } = useAuth();
  const { data: prods } = useFetch("/produtos");
  const { data: meu } = useFetch(user.role === "tecnico" ? "/estoque/tecnico" : null);
  const saldo = Object.fromEntries((meu || []).map((s) => [s.produto_id, s.qtd]));
  return (
    <div className="col-span-full">
      <SelectField label="Produto do estoque (baixa automática ao encerrar)" id={`p-prod-${i}`} placeholder="Nenhum (peça avulsa)" value={p.produto_id || ""} disabled={disabled}
        onChange={(e) => { const pr = (prods || []).find((x) => x.produto_id === e.target.value); onPick(pr ? { produto_id: pr.produto_id, descricao: pr.nome } : { produto_id: null }); }}
        options={(prods || []).map((x) => ({ value: x.produto_id, label: user.role === "tecnico" ? `${x.nome} (comigo: ${saldo[x.produto_id] || 0})` : x.nome }))} data-testid={`os-peca-produto-${i}`} />
    </div>
  );
};

const Pecas = ({ pecas, onChange, lockedCount = 0, showValor }) => {
  const upd = (i, k, v) => onChange(pecas.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const pick = (i, obj) => onChange(pecas.map((p, j) => (j === i ? { ...p, ...obj } : p)));
  return (
    <div className="space-y-3" data-testid="os-form-pecas">
      {pecas.map((p, i) => (
        <div key={i} className="grid grid-cols-[1fr_88px_48px] sm:grid-cols-[1fr_160px_100px_48px] gap-2 items-end">
          <ProdutoPick p={p} i={i} disabled={i < lockedCount} onPick={(obj) => pick(i, obj)} />
          <Field label="Peça" id={`p-d-${i}`} required value={p.descricao} onChange={(e) => upd(i, "descricao", e.target.value)} disabled={i < lockedCount} data-testid={`os-peca-descricao-${i}`} />
          <div className="hidden sm:block"><Field label="Código" id={`p-c-${i}`} value={p.codigo || ""} onChange={(e) => upd(i, "codigo", e.target.value)} disabled={i < lockedCount} data-testid={`os-peca-codigo-${i}`} /></div>
          <Field label="Qtd." id={`p-q-${i}`} type="number" step="any" min="0" value={p.quantidade} onChange={(e) => upd(i, "quantidade", e.target.value)} disabled={i < lockedCount} data-testid={`os-peca-qtd-${i}`} />
          <button type="button" onClick={() => onChange(pecas.filter((_, j) => j !== i))} className="h-14 w-12 rounded-xl border border-[#E2DDD3] flex items-center justify-center text-[#B05A40]" aria-label="Remover peça" disabled={i < lockedCount} data-testid={`os-peca-remove-${i}`}><Trash2 className="h-5 w-5" /></button>
          {showValor && <div className="col-span-full"><Field label="Valor unitário (R$)" id={`p-v-${i}`} type="number" step="0.01" min="0" value={p.valor_unitario ?? ""} onChange={(e) => upd(i, "valor_unitario", e.target.value)} data-testid={`os-peca-valor-${i}`} /></div>}
        </div>
      ))}
      <BigButton type="button" variant="outline" onClick={() => onChange([...pecas, { descricao: "", codigo: "", quantidade: 1, produto_id: null }])} data-testid="os-peca-add"><Plus className="h-5 w-5" /> Adicionar peça</BigButton>
    </div>
  );
};

export default function OSFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locked = user.role !== "admin";
  const [lockN, setLockN] = useState(0);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: clientes } = useFetch("/clientes");
  const { data: categorias } = useFetch("/categorias-servico");
  const { data: equipe } = useFetch("/ordens/equipe");
  const { data: fazendas } = useFetch(form?.cliente_id ? "/fazendas" : null, { cliente_id: form?.cliente_id });
  const { data: equips } = useFetch(form?.fazenda_id ? "/equipamentos" : null, { fazenda_id: form?.fazenda_id });

  useEffect(() => {
    if (id) {
      api.get(`/ordens/${id}`).then(({ data }) => setLockN(data.pecas?.length || 0) || setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data[k] ?? EMPTY[k]]))))
        .catch((e) => toast.error(errMsg(e)));
    } else {
      const eq = params.get("equipamento_id");
      setForm({ ...EMPTY, cliente_id: params.get("cliente_id") || "", fazenda_id: params.get("fazenda_id") || "",
        equipamento_ids: eq ? [eq] : [], tecnico_id: user.role === "tecnico" ? user.user_id : "" });
    }
  }, [id, params, user]);

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const num = (v) => (v === "" || v === null ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, km: num(form.km), horas_trabalhadas: num(form.horas_trabalhadas), valor_servico: num(form.valor_servico), categoria_id: form.categoria_id || null,
      tecnico_id: form.tecnico_id || null, pecas: form.pecas.map((p) => ({ ...p, quantidade: Number(p.quantidade) || 0, valor_unitario: p.valor_unitario === "" || p.valor_unitario == null ? null : Number(p.valor_unitario) })) };
    try {
      const { data } = id ? await api.put(`/ordens/${id}`, body) : await api.post("/ordens", body);
      toast.success(id ? "OS atualizada" : `${data.numero} criada`);
      navigate(`/ordens-servico/${data.os_id}${data.status === "finalizada" && !data.assinatura_cliente ? "?assinar=1" : ""}`, { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const cats = (categorias || []).filter((c) => c.ativo || c.categoria_id === form.categoria_id);

  return (
    <form onSubmit={submit} data-testid="os-form-page">
      <BackLink to={id ? `/ordens-servico/${id}` : "/ordens-servico"} label={id ? "Voltar para a OS" : "Ordens de Serviço"} />
      <PageHeader eyebrow={id ? "Editar" : "Nova"} title={id ? "Editar OS" : "Nova Ordem de Serviço"} subtitle="O número da OS é gerado automaticamente ao salvar." />
      <div className="space-y-5">
        <Panel title="1. Cliente e fazenda">
          <div className="grid sm:grid-cols-2 gap-4">
            <SelectField label="Cliente *" id="os-cli" required placeholder="Selecione o cliente" value={form.cliente_id}
              onChange={(e) => setForm({ ...form, cliente_id: e.target.value, fazenda_id: "", equipamento_ids: [] })}
              options={(clientes || []).map((c) => ({ value: c.cliente_id, label: c.nome }))} disabled={locked} data-testid="os-form-cliente" />
            <SelectField label="Fazenda *" id="os-faz" required placeholder={form.cliente_id ? "Selecione a fazenda" : "Escolha o cliente primeiro"} value={form.fazenda_id}
              disabled={locked || !form.cliente_id} onChange={(e) => setForm({ ...form, fazenda_id: e.target.value, equipamento_ids: [] })}
              options={(form.cliente_id ? fazendas || [] : []).map((f) => ({ value: f.fazenda_id, label: f.nome }))} data-testid="os-form-fazenda" />
          </div>
        </Panel>
        <Panel title={`2. Equipamentos envolvidos (${form.equipamento_ids.length})`}>
          {!form.fazenda_id ? <Empty>Selecione a fazenda para listar os equipamentos.</Empty> : equips?.length === 0 ? <Empty>Fazenda sem equipamentos cadastrados.</Empty> : (
            <div className="grid sm:grid-cols-2 gap-2" data-testid="os-form-equipamentos">
              {(equips || []).map((eq) => { const I = tipoIcon(eq.tipo); return (
                <Toggle disabled={locked} key={eq.equipamento_id} on={form.equipamento_ids.includes(eq.equipamento_id)} testId={`os-equip-${eq.equipamento_id}`}
                  onClick={() => setForm({ ...form, equipamento_ids: toggleIn(form.equipamento_ids, eq.equipamento_id) })}>
                  <I className="h-5 w-5 text-[#1E4D2B] shrink-0" /><span className="min-w-0"><span className="block font-semibold truncate">{eq.titulo}</span><span className="block text-xs text-[#5C6B66]">{eq.codigo}</span></span>
                </Toggle>); })}
            </div>
          )}
        </Panel>
        <Panel title="3. Serviço">
          <div className="grid gap-4">
            <SelectField label="Categoria do serviço" id="os-cat" placeholder="Selecione a categoria" value={form.categoria_id} onChange={set("categoria_id")}
              options={cats.map((c) => ({ value: c.categoria_id, label: c.nome }))} disabled={locked} data-testid="os-form-categoria" />
            <TextArea label="Serviço solicitado *" id="os-sol" required value={form.servico_solicitado} onChange={set("servico_solicitado")} disabled={locked} data-testid="os-form-servico-solicitado" />
            <TextArea label="Problema relatado" id="os-prob" value={form.problema_relatado} onChange={set("problema_relatado")} disabled={locked} data-testid="os-form-problema" />
            <TextArea label="Diagnóstico" id="os-diag" value={form.diagnostico} onChange={set("diagnostico")} data-testid="os-form-diagnostico" />
            <TextArea label="Serviço realizado" id="os-real" value={form.servico_realizado} onChange={set("servico_realizado")} data-testid="os-form-servico-realizado" />
          </div>
        </Panel>
        <Panel title="4. Equipe e agenda">
          <div className="grid sm:grid-cols-2 gap-4">
            <SelectField label="Técnico responsável" id="os-tec" placeholder="Selecione" value={form.tecnico_id} onChange={set("tecnico_id")}
              options={(equipe || []).map((u) => ({ value: u.user_id, label: u.name }))} disabled={locked} data-testid="os-form-tecnico" />
            <SelectField label="Status" id="os-status" value={form.status} onChange={set("status")} options={OS_STATUS.filter((s) => s.value !== "nao_concluida" || form.status === "nao_concluida").filter((s) => s.value !== "finalizada" || (user.role === "admin" && id && form.status === "finalizada"))} data-testid="os-form-status" />
            <Field label="Data" id="os-data" type="date" value={form.data || ""} onChange={set("data")} disabled={locked} data-testid="os-form-data" />
            <Field label="Horário" id="os-hora" type="time" value={form.horario || ""} onChange={set("horario")} disabled={locked} data-testid="os-form-horario" />
          </div>
          <p className="text-sm font-semibold mt-5 mb-2">Colaboradores</p>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="os-form-colaboradores">
            {(equipe || []).filter((u) => u.user_id !== form.tecnico_id).map((u) => (
              <Toggle disabled={locked} key={u.user_id} on={form.colaborador_ids.includes(u.user_id)} testId={`os-colab-${u.user_id}`}
                onClick={() => setForm({ ...form, colaborador_ids: toggleIn(form.colaborador_ids, u.user_id) })}>{u.name}</Toggle>
            ))}
          </div>
        </Panel>
        <Panel title="5. Execução">
          <div className="grid grid-cols-2 gap-4 mb-5">
            <Field label="Quilometragem (km)" id="os-km" type="number" step="any" min="0" inputMode="decimal" value={form.km ?? ""} onChange={set("km")} data-testid="os-form-km" />
            <Field label="Horas trabalhadas" id="os-horas" type="number" step="any" min="0" inputMode="decimal" value={form.horas_trabalhadas ?? ""} onChange={set("horas_trabalhadas")} data-testid="os-form-horas" />
          </div>
          {!locked && <div className="mb-5"><Field label="Valor do serviço (R$) — visível só para administrador e cliente" id="os-valor" type="number" step="0.01" min="0" inputMode="decimal" value={form.valor_servico ?? ""} onChange={set("valor_servico")} data-testid="os-form-valor-servico" /></div>}
          <p className="text-sm font-semibold mb-2">Peças utilizadas</p>
          <Pecas showValor={!locked} lockedCount={locked ? lockN : 0} pecas={form.pecas} onChange={(pecas) => setForm({ ...form, pecas })} />
          <div className="mt-5"><TextArea label="Observações" id="os-obs" value={form.observacoes} onChange={set("observacoes")} data-testid="os-form-observacoes" /></div>
          {locked && <p className="mt-4 text-xs text-[#B05A40]" data-testid="os-form-locked-hint">Os dados definidos pelo administrador não podem ser alterados. Você pode complementar diagnóstico, serviço realizado e observações (sem apagar o que já foi registrado) e adicionar peças, km e horas.</p>}
          <p className="mt-4 text-xs text-[#5C6B66]">Fotos e assinaturas são adicionadas na tela da OS após salvar.</p>
        </Panel>
        <div className="sticky bottom-20 md:bottom-4 z-20">
          <BigButton type="submit" disabled={busy} className="shadow-xl shadow-[#1E4D2B]/20" data-testid="os-form-submit">{busy && <Loader2 className="h-5 w-5 animate-spin" />} {id ? "Salvar alterações" : "Criar OS"}</BigButton>
        </div>
      </div>
    </form>
  );
}
